import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  TextField,
  Grid,
  Typography,
  CircularProgress,
} from "@mui/material";
import { 
  crearHorarioCliente, 
  editarHorarioCliente 
} from "../../http/actions/HorarioClienteAction";
import { getClienteHabilitado } from "../../http/actions/ClientesAction";
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

const CrearHorarioClienteModal = ({ open, onClose, onSubmit, horarioEditar }) => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    horarioIds: [],
    clienteId: '',
    dias: [],
    horaEnvio: null
  });
  const [errores, setErrores] = useState({});

  // Días de la semana para seleccionar
  const diasSemana = [
    { id: 1, nombre: 'Lunes' },
    { id: 2, nombre: 'Martes' },
    { id: 3, nombre: 'Miércoles' },
    { id: 4, nombre: 'Jueves' },
    { id: 5, nombre: 'Viernes' },
    { id: 6, nombre: 'Sábado' },
    { id: 7, nombre: 'Domingo' }
  ];

  // Cargar clientes habilitados
  useEffect(() => {
    if (open) {
      setLoading(true);
      getClienteHabilitado(localStorage.getItem("token"))
        .then((response) => {
          setClientes(Array.isArray(response) ? response : []);
        })
        .catch((error) => {
          console.error("Error al cargar clientes:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open]);

  // Inicializar datos de edición si existen
  useEffect(() => {
    if (horarioEditar) {
      let horaDate = null;
      if (horarioEditar.horaEnvio) {
        const today = new Date();
        const horaString = horarioEditar.horaEnvio.substring(0, 5);
        const [hours, minutes] = horaString.split(':');
        today.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
        horaDate = today;
      }

      setFormData({
        horarioIds: horarioEditar.horarioIds || [],
        clienteId: horarioEditar.clienteId,
        dias: horarioEditar.dias || [],
        horaEnvio: horaDate
      });
    } else {
      setFormData({
        horarioIds: [],
        clienteId: '',
        dias: [],
        horaEnvio: null
      });
    }
    setErrores({});
  }, [horarioEditar, open]);

  const validarFormulario = () => {
    const nuevosErrores = {};
    
    if (!formData.clienteId) {
      nuevosErrores.clienteId = "Debe seleccionar un cliente";
    }
    
    if (formData.dias.length === 0) {
      nuevosErrores.dias = "Debe seleccionar al menos un día";
    }
    
    if (!formData.horaEnvio) {
      nuevosErrores.horaEnvio = "Debe seleccionar una hora";
    }
    
    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = async () => {
    if (!validarFormulario()) {
      return;
    }

    setLoading(true);
    
    try {
      let horaString = "";
      if (formData.horaEnvio) {
        horaString = `${formData.horaEnvio.getHours().toString().padStart(2, '0')}:${formData.horaEnvio.getMinutes().toString().padStart(2, '0')}:00`;
      }

      const horarioData = {
        horarioIds: formData.horarioIds, // Incluir los IDs
        clienteId: formData.clienteId,
        dias: formData.dias,
        horaEnvio: horaString
      };

      if (horarioEditar) {
        await editarHorarioCliente(localStorage.getItem("token"), horarioData);
      } else {
        await crearHorarioCliente(localStorage.getItem("token"), horarioData);
      }
      
      onSubmit(horarioData);
    } catch (error) {
      console.error("Error al guardar horario:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {horarioEditar ? "Editar Horario de Cliente" : "Configurar Nuevo Horario de Cliente"}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errores.clienteId}>
              <InputLabel>Cliente</InputLabel>
              <Select
                value={formData.clienteId}
                onChange={(e) => setFormData({ ...formData, clienteId: e.target.value })}
                label="Cliente"
                disabled={!!horarioEditar || loading}
              >
                {clientes.map((cliente) => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    {cliente.nombre}
                  </MenuItem>
                ))}
              </Select>
              {errores.clienteId && (
                <Typography color="error" variant="caption">
                  {errores.clienteId}
                </Typography>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Días de envío
            </Typography>
            {errores.dias && (
              <Typography color="error" variant="caption" display="block" sx={{ mb: 1 }}>
                {errores.dias}
              </Typography>
            )}
            <FormGroup row>
              {diasSemana.map((dia) => (
                <FormControlLabel
                  key={dia.id}
                  control={
                    <Checkbox
                      checked={formData.dias.includes(dia.id)}
                      onChange={() => {
                        const nuevoDias = formData.dias.includes(dia.id)
                          ? formData.dias.filter(d => d !== dia.id)
                          : [...formData.dias, dia.id];
                        setFormData({ ...formData, dias: nuevoDias });
                      }}
                      disabled={loading}
                    />
                  }
                  label={dia.nombre}
                />
              ))}
            </FormGroup>
          </Grid>
          
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <TimePicker
                label="Hora de envío"
                value={formData.horaEnvio}
                onChange={(newValue) => setFormData({ ...formData, horaEnvio: newValue })}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    error={!!errores.horaEnvio}
                    helperText={errores.horaEnvio}
                  />
                )}
                disabled={loading}
                ampm={false}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : horarioEditar ? (
            "Actualizar"
          ) : (
            "Guardar"
          )}
        </Button>
        <Button onClick={onClose} variant="contained" color="error" disabled={loading}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrearHorarioClienteModal;